/// <reference types="google.maps" />
import { useEffect, useMemo, type FC } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import { Flex } from 'antd';
import { EnvironmentTwoTone, HomeTwoTone } from '@ant-design/icons';
import { Button, Text } from '@g2u/ui-kit';

import env from 'env';
import { ServiceAreaHomeSuburb, ServiceAreaSuburb, ServiceAreaType, Suburb } from 'types';
import styles from './technician-service-area-map.module.scss';

type Props = Readonly<{
  suburbs: ServiceAreaSuburb[];
  home: ServiceAreaHomeSuburb | null;
  assignableSuburbs: Suburb[];
  onSave: () => void;
  isSaving?: boolean;
  isSaveDisabled?: boolean;
  isCandidateSearchPending?: boolean;
}>;

type MarkerType = 'primary' | 'secondary' | 'unclassified' | 'home';

type MarkerData = {
  type: MarkerType;
  lat: number;
  lon: number;
  name: string;
};

// Matches the design system's Red/6, Gold/6, and Purple/6 tokens used in the Figma mock.
const MARKER_COLORS: Record<'primary' | 'secondary' | 'unclassified', string> = {
  primary: '#f5222d',
  secondary: '#faad14',
  unclassified: '#722ed1',
};

// AntD's own generated tint for each colour above (the light background a TwoTone icon draws behind its main
// shape) — computed with @ant-design/colors' generate(), the same function the real icon components call.
const MARKER_SECONDARY_COLORS: Record<'primary' | 'secondary' | 'unclassified', string> = {
  primary: '#fff1f0',
  secondary: '#fffbe6',
  unclassified: '#f9f0ff',
};

// HomeTwoTone's own default colouring when no twoToneColor is passed — AntD's blue token and its generated tint.
const HOME_MARKER_COLOR = '#1677ff';
const HOME_MARKER_SECONDARY_COLOR = '#e6f4ff';

const ICON_VIEW_BOX = '64 64 896 896';
const ICON_SIZE = 24;

// Same path data @ant-design/icons-svg ships for EnvironmentTwoTone/HomeTwoTone. A Google Marker icon needs a
// flat image rather than a live React component, so the same paths are drawn into a static SVG here instead.
const ENVIRONMENT_ICON_PATHS: ReadonlyArray<{ fill: 'primary' | 'secondary'; d: string }> = [
  {
    fill: 'secondary',
    d: 'M724.4 224.9C667.7 169.5 592.3 139 512 139s-155.7 30.5-212.4 85.8C243.1 280 212 353.2 212 431.1c0 241.3 234.1 407.2 300 449.1 65.9-41.9 300-207.8 300-449.1 0-77.9-31.1-151.1-87.6-206.2zM512 615c-97.2 0-176-78.8-176-176s78.8-176 176-176 176 78.8 176 176-78.8 176-176 176z',
  },
  {
    fill: 'primary',
    d: 'M512 263c-97.2 0-176 78.8-176 176s78.8 176 176 176 176-78.8 176-176-78.8-176-176-176zm79.2 255.2A111.6 111.6 0 01512 551c-29.9 0-58-11.7-79.2-32.8A111.6 111.6 0 01400 439c0-29.9 11.7-58 32.8-79.2C454 338.6 482.1 327 512 327c29.9 0 58 11.6 79.2 32.8S624 409.1 624 439c0 29.9-11.6 58-32.8 79.2z',
  },
  {
    fill: 'primary',
    d: 'M854.6 289.1a362.49 362.49 0 00-79.9-115.7 370.83 370.83 0 00-118.2-77.8C610.7 76.6 562.1 67 512 67c-50.1 0-98.7 9.6-144.5 28.5-44.3 18.3-84 44.5-118.2 77.8A363.6 363.6 0 00169.4 289c-19.5 45-29.4 92.8-29.4 142 0 70.6 16.9 140.9 50.1 208.7 26.7 54.5 64 107.6 111 158.1 80.3 86.2 164.5 138.9 188.4 153a43.9 43.9 0 0022.4 6.1c7.8 0 15.5-2 22.4-6.1 23.9-14.1 108.1-66.8 188.4-153 47-50.4 84.3-103.6 111-158.1C867.1 572 884 501.8 884 431.1c0-49.2-9.9-97-29.4-142zM512 880.2c-65.9-41.9-300-207.8-300-449.1 0-77.9 31.1-151.1 87.6-206.3C356.3 169.5 431.7 139 512 139s155.7 30.5 212.4 85.9C780.9 280 812 353.2 812 431.1c0 241.3-234.1 407.2-300 449.1z',
  },
];

const HOME_ICON_PATHS: ReadonlyArray<{ fill: 'primary' | 'secondary'; d: string }> = [
  {
    fill: 'secondary',
    d: 'M512.1 172.6l-370 369.7h96V868H392V640c0-22.1 17.9-40 40-40h160c22.1 0 40 17.9 40 40v228h153.9V542.3H882L535.2 195.7l-23.1-23.1zm434.5 422.9c-6 6-13.1 10.8-20.8 13.9 7.7-3.2 14.8-7.9 20.8-13.9zm-887-34.7c5 30.3 31.4 53.5 63.1 53.5h.9c-31.9 0-58.9-23-64-53.5zm-.9-10.5v-1.9 1.9zm.1-2.6c.1-3.1.5-6.1 1-9.1-.6 2.9-.9 6-1 9.1z',
  },
  {
    fill: 'primary',
    d: 'M951 510c0-.1-.1-.1-.1-.2l-1.8-2.1c-.1-.1-.2-.3-.4-.4-.7-.8-1.5-1.6-2.2-2.4L560.1 118.8l-25.9-25.9a31.5 31.5 0 00-44.4 0L77.5 505a63.6 63.6 0 00-16 26.6l-.6 2.1-.3 1.1-.3 1.2c-.2.7-.3 1.4-.4 2.1 0 .1 0 .3-.1.4-.6 3-.9 6-1 9.1v3.3c0 .5 0 1 .1 1.5 0 .5 0 .9.1 1.4 0 .5.1 1 .1 1.5 0 .6.1 1.2.2 1.8 0 .3.1.6.1.9l.3 2.5v.1c5.1 30.5 32.2 53.5 64 53.5h42.5V940h691.7V614.3h43.4c8.6 0 16.9-1.7 24.5-4.9s14.7-7.9 20.8-13.9a63.6 63.6 0 0018.7-45.3c0-14.7-5-28.8-14.3-40.2zM568 868H456V664h112v204zm217.9-325.7V868H632V640c0-22.1-17.9-40-40-40H432c-22.1 0-40 17.9-40 40v228H238.1V542.3h-96l370-369.7 23.1 23.1L882 542.3h-96.1z',
  },
];

const buildMarkerIconUrl = (type: MarkerType): string => {
  const isHome = type === 'home';
  const paths = isHome ? HOME_ICON_PATHS : ENVIRONMENT_ICON_PATHS;
  const primaryColor = isHome ? HOME_MARKER_COLOR : MARKER_COLORS[type];
  const secondaryColor = isHome ? HOME_MARKER_SECONDARY_COLOR : MARKER_SECONDARY_COLORS[type];

  const pathElements = paths
    .map(({ fill, d }) => `<path fill="${fill === 'primary' ? primaryColor : secondaryColor}" d="${d}"/>`)
    .join('');

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${ICON_VIEW_BOX}" width="${ICON_SIZE}" height="${ICON_SIZE}">${pathElements}</svg>`;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Hides Google's default POI/business icons so they don't visually compete with our own suburb markers.
const MAP_STYLES: google.maps.MapTypeStyle[] = [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }];

const FIT_BOUNDS_MAX_ZOOM = 14;
const FIT_BOUNDS_PADDING = 40;
const HOME_ONLY_ZOOM = 11;

// suburbs.lat/lon are NOT NULL decimal columns, so a suburb with no coordinates on record comes through as the
// zero default ('0.000000'), not null — 0 is never a real coordinate for an Australian suburb either way. We
// still accept null/'' here too, just in case a future caller feeds this something looser than the API shape.
// Letting a zero through would plant a marker off the coast of Africa and drag fitBounds out to half the planet.
const hasCoordinates = (lat: string | null, lon: string | null): boolean =>
  Number.isFinite(Number(lat)) && Number(lat) !== 0 && Number.isFinite(Number(lon)) && Number(lon) !== 0;

type MapMarkersProps = Readonly<{
  allMarkers: MarkerData[];
  suburbMarkers: MarkerData[];
  homeMarker: MarkerData | null;
  isCandidateSearchPending: boolean;
}>;

// fitBounds has no maxZoom option of its own — cap it after the fact once the bounds change actually lands,
// otherwise one or two very-close markers can zoom in absurdly far (street-level). Returns the listener so the
// caller can remove it if the bounds change never actually fires before the map moves on to something else.
const fitMapToMarkers = (
  map: google.maps.Map,
  allMarkers: MarkerData[],
  suburbMarkers: MarkerData[],
  homeMarker: MarkerData | null,
  isCandidateSearchPending: boolean,
): google.maps.MapsEventListener | undefined => {
  if (isCandidateSearchPending) {
    return undefined;
  }

  if (suburbMarkers.length > 0) {
    const bounds = new google.maps.LatLngBounds();
    allMarkers.forEach((markerData) => bounds.extend({ lat: markerData.lat, lng: markerData.lon }));
    const zoomCapListener = google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      const zoom = map.getZoom();
      if (zoom !== undefined && zoom > FIT_BOUNDS_MAX_ZOOM) {
        map.setZoom(FIT_BOUNDS_MAX_ZOOM);
      }
    });
    map.fitBounds(bounds, FIT_BOUNDS_PADDING);
    return zoomCapListener;
  }

  if (homeMarker) {
    map.setCenter({ lat: homeMarker.lat, lng: homeMarker.lon });
    map.setZoom(HOME_ONLY_ZOOM);
  }

  return undefined;
};

// Markers live on the classic google.maps.Marker API (not <AdvancedMarker>, which needs a real Cloud-Console
// Map ID this design deliberately avoids) — useMap() is how a descendant of <Map> gets the raw map instance
// to hand them to.
const MapMarkers: FC<MapMarkersProps> = ({ allMarkers, suburbMarkers, homeMarker, isCandidateSearchPending }) => {
  const map = useMap();

  useEffect(() => {
    if (!map) {
      return;
    }

    const infoWindow = new google.maps.InfoWindow({ disableAutoPan: true });

    const createdMarkers = allMarkers.map((markerData) => {
      const marker = new google.maps.Marker({
        position: { lat: markerData.lat, lng: markerData.lon },
        map,
        icon: {
          url: buildMarkerIconUrl(markerData.type),
          scaledSize: new google.maps.Size(ICON_SIZE, ICON_SIZE),
          anchor: new google.maps.Point(ICON_SIZE / 2, ICON_SIZE),
        },
      });

      if (markerData.type !== 'home') {
        marker.addListener('mouseover', () => {
          infoWindow.setContent(markerData.name);
          infoWindow.open({ map, anchor: marker });
        });
        marker.addListener('mouseout', () => infoWindow.close());
      }

      return marker;
    });

    const zoomCapListener = fitMapToMarkers(map, allMarkers, suburbMarkers, homeMarker, isCandidateSearchPending);

    return () => {
      zoomCapListener?.remove();
      createdMarkers.forEach((marker) => marker.setMap(null));
      infoWindow.close();
    };
  }, [map, allMarkers, suburbMarkers, homeMarker, isCandidateSearchPending]);

  // AntD's Tabs keeps every tab mounted and hides inactive ones with display:none rather than unmounting them
  // (no destroyOnHidden set), so the marker-sync effect above can end up fitting against a 0x0 container while
  // this tab is hidden. That produces a wrong zoom that doesn't correct itself once the container is visible
  // again — it takes a fresh fit, which is what this effect provides the moment that happens.
  useEffect(() => {
    if (!map) {
      return;
    }

    const container = map.getDiv();
    let zoomCapListener: google.maps.MapsEventListener | undefined;
    let wasHidden = container.clientWidth === 0 || container.clientHeight === 0;

    const observer = new ResizeObserver(() => {
      const isHidden = container.clientWidth === 0 || container.clientHeight === 0;

      if (wasHidden && !isHidden) {
        google.maps.event.trigger(map, 'resize');
        zoomCapListener?.remove();
        zoomCapListener = fitMapToMarkers(map, allMarkers, suburbMarkers, homeMarker, isCandidateSearchPending);
      }

      wasHidden = isHidden;
    });

    observer.observe(container);

    return () => {
      zoomCapListener?.remove();
      observer.disconnect();
    };
  }, [map, allMarkers, suburbMarkers, homeMarker, isCandidateSearchPending]);

  return null;
};

export const TechnicianServiceAreaMap: FC<Props> = ({
  suburbs,
  home,
  assignableSuburbs,
  onSave,
  isSaving,
  isSaveDisabled,
  isCandidateSearchPending = false,
}) => {
  // Memoized on the raw props rather than recomputed every render: MapMarkers' effect depends on these
  // arrays, and a save/search-unrelated re-render (isSaving toggling, a Transfer selection changing) would
  // otherwise hand it new array references every time, tearing down and re-fitting the map for no reason.
  const suburbMarkers = useMemo(() => {
    const markers: MarkerData[] = [];

    suburbs.forEach((suburb) => {
      if (!hasCoordinates(suburb.lat, suburb.lon)) {
        return;
      }

      markers.push({
        type: suburb.primary_secondary === ServiceAreaType.Primary ? 'primary' : 'secondary',
        lat: Number(suburb.lat),
        lon: Number(suburb.lon),
        name: suburb.suburb,
      });
    });

    // Candidates stay empty until the first search settles.
    assignableSuburbs.forEach((suburb) => {
      if (!hasCoordinates(suburb.lat, suburb.lon)) {
        return;
      }

      markers.push({ type: 'unclassified', lat: Number(suburb.lat), lon: Number(suburb.lon), name: suburb.suburb });
    });

    return markers;
  }, [suburbs, assignableSuburbs]);

  const homeMarker: MarkerData | null = useMemo(
    () =>
      home && hasCoordinates(home.lat, home.lon)
        ? { type: 'home', lat: Number(home.lat), lon: Number(home.lon), name: home.suburb }
        : null,
    [home],
  );

  const allMarkers = useMemo(
    () => (homeMarker ? [...suburbMarkers, homeMarker] : suburbMarkers),
    [suburbMarkers, homeMarker],
  );
  const showEmptyState = !isCandidateSearchPending && suburbMarkers.length === 0 && homeMarker === null;

  return (
    <div className={styles.map}>
      {showEmptyState ? (
        <div className={styles.map__empty}>
          <Text.$>No location data available for this technician.</Text.$>
        </div>
      ) : (
        <APIProvider apiKey={env.GOOGLE_MAPS_API_KEY as string}>
          <Map
            className={styles.map__canvas}
            defaultCenter={homeMarker ? { lat: homeMarker.lat, lng: homeMarker.lon } : { lat: 0, lng: 0 }}
            defaultZoom={HOME_ONLY_ZOOM}
            styles={MAP_STYLES}
          >
            <MapMarkers
              allMarkers={allMarkers}
              suburbMarkers={suburbMarkers}
              homeMarker={homeMarker}
              isCandidateSearchPending={isCandidateSearchPending}
            />
          </Map>
        </APIProvider>
      )}
      <Flex wrap gap={8} align="center" className={styles.map__legend}>
        <Flex gap={4} align="center">
          <EnvironmentTwoTone style={{ fontSize: 20 }} twoToneColor={MARKER_COLORS.primary} />
          <Text.$>Primary Area</Text.$>
        </Flex>
        <Flex gap={4} align="center">
          <EnvironmentTwoTone style={{ fontSize: 20 }} twoToneColor={MARKER_COLORS.secondary} />
          <Text.$>Secondary Area</Text.$>
        </Flex>
        <Flex gap={4} align="center">
          <EnvironmentTwoTone style={{ fontSize: 20 }} twoToneColor={MARKER_COLORS.unclassified} />
          <Text.$>Unclassified Area</Text.$>
        </Flex>
        <Flex gap={4} align="center">
          <HomeTwoTone style={{ fontSize: 20 }} />
          <Text.$>Technician Home</Text.$>
        </Flex>
      </Flex>
      <Flex justify="flex-end">
        <Button.$
          type={Button.Type.Primary}
          loading={isSaving}
          disabled={isSaveDisabled}
          onClick={onSave}
          dataTestId="save-service-area-button"
        >
          Save
        </Button.$>
      </Flex>
    </div>
  );
};
